// src/components/store/QuickAddressComponents/DeliveryAddressSelector.jsx
import React from "react";
import { motion } from "framer-motion";
import { Button } from "@radix-ui/themes";
import { Loader, Plus, MapPin, Check } from "lucide-react";
import { useUserAddresses } from "../../../hooks/useUserAddresses";
import { useGoogleMaps } from "../../../hooks/useGoogleMaps";
import primaryCoveragePolygon from "../../pages/coverageComponents/perimeter.json";
import { Badge } from "./Badge";
import DividerComponent from "../../ui/DividerComponent";

export const DeliveryAddressSelector = ({ onNewAddress }) => {
  const {
    addresses,
    isLoading,
    selectedAddressId,
    selectAddress,
    isSelectingAddress,
  } = useUserAddresses();

  const { validateLocation } = useGoogleMaps(primaryCoveragePolygon, true);

  const getAddressZone = (address) => {
    try {
      if (!validateLocation || !address?.latitude || !address?.longitude) {
        return null;
      }
      const coords = { lat: address.latitude, lng: address.longitude };
      const validationResult = validateLocation(coords);
      return validationResult?.zone || null;
    } catch (error) {
      console.error("Error validando zona de dirección:", error);
      return null;
    }
  };

  const handleAddressClick = (address) => {
    if (isSelectingAddress) return;
    if (selectedAddressId !== address.id) {
      selectAddress(address.id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader className="animate-spin mr-2" />
        <span>Cargando direcciones...</span>
      </div>
    );
  }

  if (addresses.length === 0) {
    return (
      <div className="text-center p-6 bg-gray-50 rounded-lg">
        <p className="text-gray-600 mb-4">No tienes direcciones guardadas</p>
        <Button
          onClick={onNewAddress}
          className="flex items-center gap-1 mx-auto"
        >
          <Plus size={16} /> Agregar dirección
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-gray-800 text-lg font-normal">
          Tus direcciones guardadas
        </h3>
        <Button
          variant="outline"
          onClick={onNewAddress}
          className="flex items-center gap-1 text-sm"
          size={"3"}
        >
          <Plus size={16} /> Nueva dirección
        </Button>
      </div>

      <div className="space-y-3">
        {addresses.map((address) => {
          const isCurrentlySelected = selectedAddressId === address.id;
          const addressZone = getAddressZone(address);

          return (
            <motion.div
              key={address.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className={`p-2 md:p-4 rounded-xl flex-col flex items-start gap-4 shadow-sm overflow-hidden cursor-pointer relative transition-colors duration-200 ${
                isCurrentlySelected
                  ? "bg-blue-50 border-2 border-blue-200"
                  : "bg-gray-50 hover:bg-gray-100 border-2 border-transparent"
              } ${
                isSelectingAddress && selectedAddressId === address.id
                  ? "opacity-50 pointer-events-none"
                  : ""
              }`}
              onClick={() => handleAddressClick(address)}
            >
              <div className="flex gap-2 items-center w-full">
                {address.image_url ? (
                  <div className="min-w-16 max-w-16 h-16 rounded-xl overflow-hidden justify-center items-center flex">
                    <img
                      src={address.image_url}
                      alt="Mapa de la dirección"
                      className="w-48 h-48 rounded-md mr-2 object-cover"
                    />
                  </div>
                ) : (
                  <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0 mt-1 h-24 w-24 flex items-center justify-center">
                    <MapPin className="w-8 h-8 text-blue-600" />
                  </div>
                )}
                <div className="flex flex-col flex-1">
                  <h3 className="text-gray-900 text-base md:text-lg line-clamp-2 font-medium">
                    {address.address || "Dirección no especificada"}
                  </h3>
                  {address.details && (
                    <p className="text-sm text-gray-600 mt-1 line-clamp-1">
                    {address.details}
                    </p>
                  )}
                  <p className="text-sm text-gray-500">{address.city}</p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {isCurrentlySelected && (
                    <div className="flex min-h-8 min-w-8 text-blue-600 font-medium items-center justify-center text-xs md:text-base bg-blue-100 rounded-full">
                      <Check size={20} />
                    </div>
                  )}
                </div>
              </div>

              <DividerComponent color="ebebeb" />

              <div className="flex items-center gap-2">
                <p className="text-sm md:text-base text-nowrap">
                  Costo de entrega:
                </p>
                <Badge
                  text={
                    addressZone === 1
                      ? "Gratis"
                      : addressZone === 2
                      ? "$3.500 por botellón"
                      : "$7.000 por botellón"
                  }
                  zone={addressZone}
                />
              </div>

              {isSelectingAddress && selectedAddressId === address.id && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-xl">
                  <Loader className="animate-spin h-6 w-6 text-blue-600" />
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default DeliveryAddressSelector;
